// src/components/Product/ProductHeader.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Share2, FileText } from 'lucide-react';

import { useAuthContext } from '../../context/AuthContext';
import { toggleProductLike } from '../../services/profileService';

import AccessGate from './AccessGate';
import { convertToPersianNumbers } from '../../utils/PersianNumberUtils';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const SAFE_FALLBACK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const SimpleImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);
  const imageSrc = hasError || !src ? SAFE_FALLBACK : src;
  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
};

const ProductHeader = ({ product, navigateToBuy }) => {
  const { isLoggedIn, openAuthModal, triggerProfileUpdate } = useAuthContext();

  const [isLiked, setIsLiked] = useState(product?.is_liked_by_user || false);
  const [likesCount, setLikesCount] = useState(product?.likes_count || 0);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [likeError, setLikeError] = useState(null);
  const [shareMessage, setShareMessage] = useState(null);

  useEffect(() => {
    if (product) {
      setIsLiked(product.is_liked_by_user || false);
      setLikesCount(product.likes_count || 0);
    }
  }, [product]);

  if (!product) return null;

  const {
    title = 'بدون عنوان',
    description = '',
    cover_url = null,
    price = null,
    price_display = '',
    is_free = false,
    is_new = false,
    categories = [],
    tags = [],
    has_access = false,
    long_description = '',
  } = product;

  const persianPrice = price_display?.trim()
    ? convertToPersianNumbers(price_display)
    : (price != null ? convertToPersianNumbers(price) : 'تماس بگیرید');

  const isFreeContent = is_free || (price != null && price <= 0);

  const shouldShowPriceLabel = isFreeContent || (!is_free && !has_access);
  const priceLabelBgClass = isFreeContent ? 'bg-cyan-700' : 'bg-indigo-700';

  const handleLikeClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLikeError(null);
    if (!isLoggedIn) {
      openAuthModal?.();
      return;
    }
    if (isLikeLoading) return;
    setIsLikeLoading(true);
    try {
      const response = await toggleProductLike(product.id);
      if (response) {
        setIsLiked(response.is_liked);
        setLikesCount(response.new_likes_count);
        triggerProfileUpdate();
      }
    } catch (error) {
      console.error('خطا در لایک:', error);
      setLikeError('متأسفانه خطایی رخ داد. لطفاً مجدد تلاش کنید.');
    } finally {
      setIsLikeLoading(false);
    }
  };

  const handleShare = async () => {
    const currentUrl = window.location.href;
    const shareTitle = title || 'محصول';
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: `مشاهده محصول "${shareTitle}"`, url: currentUrl });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') console.error('خطا در Web Share API:', err);
        if (err.name === 'AbortError') return;
      }
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = currentUrl;
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, 99999);
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      setShareMessage(successful ? '✅ لینک محصول در کلیپ‌بورد کپی شد.' : '❌ کپی لینک انجام نشد. لطفاً دستی کپی کنید.');
    } catch (err) {
      console.error('خطا در کپی لینک:', err);
      setShareMessage('❌ کپی لینک انجام نشد. لطفاً دستی کپی کنید.');
    }
    setTimeout(() => setShareMessage(null), 3000);
  };

  const handleBuyClick = () => {
    if (!isLoggedIn) {
      openAuthModal?.();
      return;
    }
    if (typeof navigateToBuy === 'function') navigateToBuy();
    else console.error('❌ ProductHeader: navigateToBuy is not a function', navigateToBuy);
  };

  return (
    <header className="product-header-section rtl text-right bg-white rounded-xl border border-gray-200 p-4">
      <div className="mb-3 text-xs text-gray-500 overflow-hidden whitespace-nowrap overflow-ellipsis">
        <Link to="/" className="hover:text-indigo-600">خانه</Link>
        {categories?.length > 0 && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/products/list/${categories[0]?.slug}`} className="hover:text-indigo-600">
              {categories[0]?.name}
            </Link>
          </>
        )}
        <span className="mx-2">/</span>
        <span className="text-gray-800 font-medium">{title}</span>
      </div>

      <h1 className="text-xl font-extrabold text-gray-900 mb-4">{title}</h1>

      <div className="flex flex-row gap-4 items-start">
        <div className="w-2/5 flex-shrink-0 flex flex-col gap-4">
          <div className="relative w-full pb-[100%] rounded-xl overflow-hidden bg-gray-100">
            <SimpleImage src={cover_url} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            {is_new && (
              <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-md z-10">
                جدید
              </span>
            )}
            {shouldShowPriceLabel && (
              <span className={`absolute bottom-0 left-0 ${priceLabelBgClass} text-white text-[10px] font-light px-3 py-1 rounded-tr-lg z-10`}>
                {isFreeContent ? 'رایگان' : persianPrice}
              </span>
            )}
          </div>

          {long_description && (
            <Link
              to={`/products/${product.slug}/summary`}
              className="w-full flex items-center justify-center py-3 px-4 rounded-xl font-bold text-[11px] bg-green-700 text-white hover:bg-green-800 transition"
            >
              <FileText size={16} className="ml-2" />
              چکیده محتوا
            </Link>
          )}
        </div>

        <div className="w-3/5 flex flex-col">
          {description && (
            <div
              className="text-gray-700 text-[11px] leading-relaxed mb-4 line-clamp-[10] text-justify"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            />
          )}
          {likeError && <div className="text-red-500 text-xs mb-2">{likeError}</div>}
          {shareMessage && <div className="text-green-600 text-xs mb-2">{shareMessage}</div>}

          <div className="mt-auto flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleLikeClick}
                disabled={isLikeLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${isLikeLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
              >
                <Heart size={20} className={`${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                <span className="text-[11px] font-medium">{convertToPersianNumbers(likesCount)}</span>
              </button>
              <button onClick={handleShare} className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition">
                <Share2 size={20} className="text-gray-600" />
                <span className="text-[11px] font-medium">اشتراک</span>
              </button>
            </div>

            <AccessGate
              hasAccess={has_access}
              isFree={isFreeContent}
              onBuyClick={handleBuyClick}
              priceDisplay={persianPrice}
            />
          </div>
        </div>
      </div>

      {tags?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2 justify-start">
            {tags.map((tag) => (
              <Link
                key={tag.slug}
                to={`/products/list/tag/${tag.slug}`}
                className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full hover:bg-green-100 transition"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default ProductHeader;